import React, { useRef, useEffect } from 'react';
import { Navigation } from './components/Navigation/Navigation';
import { HeroSection } from './components/Hero/HeroSection';
import { IngredientsSection } from './components/Ingredients/IngredientsSection';
import { ProductSection } from './components/Product/ProductSection';
import { FAQSection } from './components/FAQ/FAQSection';
import { Footer } from './components/Footer';
import { CartDrawer } from './components/Cart/CartDrawer';
import TermsPage from './pages/terms';
import PrivacyPage from './pages/privacy';
import { CartProvider } from './context/CartContext';
import { SEO } from './components/SEO';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { FlavorProvider } from './context/FlavorContext';
import AboutPage from './pages/about';
import FAQsPage from './pages/faqs';
import FAPPage from './pages/fap';
import ContactPage from './pages/contact';
import ReturnFormPage from './pages/return-form';
import ReturnsPage from './pages/returns';
import DisclaimerPage from './pages/disclaimer';
import CancelPage from './pages/cancel';
import { BenefitsSection } from './components/Benefits/BenefitsSection';
import SuccessPage from './pages/success';
import EssentialPage from './pages/essential';
import DynamicPage from './pages/dynamic';
import TriplePackPage from './pages/triple-pack';
import { useFacebookPixel } from './hooks/useFacebookPixel';

const PixelTracker: React.FC = () => {
  useFacebookPixel();
  return null;
};

// Main layout component
const MainLayout: React.FC = () => {
  const productSectionRef = useRef<HTMLDivElement>(null);

  const scrollToProduct = () => {
    productSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.location.hash === '#product-section') {
      const timer = setTimeout(() => {
        productSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <CartProvider>
      <SEO
        title="Premium Hydration & Vitamin Recovery Blend"
        description="Natural, scientifically formulated supplements to support your wellbeing after raves and festivals. Free shipping on 2+ tubs."
        keywords="rave recovery supplements, post-rave recovery, festival recovery, electrolyte supplements, post event recovery, rave remedy, rave supplements, post rave vitamins, recovery supplements for festival goers"
        canonical="https://raveremedy.com/"
      />
      <div className="min-h-screen bg-gradient-to-b from-black via-purple-900/20 to-black text-white">
        <Navigation scrollToProduct={scrollToProduct} />
        <HeroSection />
        <IngredientsSection />
        <div ref={productSectionRef}>
          <ProductSection />
        </div>
        <BenefitsSection />
        <FAQSection />
        <Footer />
        <CartDrawer />
      </div>
    </CartProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <PixelTracker />
      <FlavorProvider>
        <Routes>
          <Route path="/" element={<MainLayout />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faqs" element={<FAQsPage />} />
          <Route path="/fap" element={<FAPPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/return-form" element={<ReturnFormPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/cancel" element={<CancelPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/essential" element={<EssentialPage />} />
          <Route path="/dynamic" element={<DynamicPage />} />
          <Route path="/triple-pack" element={<TriplePackPage />} />
        </Routes>
      </FlavorProvider>
    </Router>
  );
};

export default App;
